import { useCallback, useContext, type MouseEvent } from 'react'
import { defineMessages, useIntl, type MessageDescriptor } from 'react-intl'

import CruiseOfferTileInclusionsModal from './CruiseOfferTileInclusionsModal'
import OfferUrgencyCountdownLabel, { type TagPlace } from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import {
  CRUISE_BUNDLE_FLIGHT_CREDITS_LABEL,
  CRUISE_BUNDLE_HOTEL_FLIGHT_CREDITS_LABEL,
  CRUISE_BUNDLE_HOTEL_LABEL,
  CRUISE_FAMILY_PRICING_LABEL,
  CRUISE_OFFER_COUNTDOWN_LABEL,
  CRUISE_SPECIAL_OFFER_LABEL,
} from 'constants/cruise'
import {
  type CruiseBundleImageCornerLabel,
  type CruiseOfferView,
} from 'hooks/Cruise/useCruiseOfferView'
import IconButton from 'LuxKit/Button/IconButton'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import HighlightLabel from 'LuxKit/Label/HighlightLabel'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import { isCruiseOffer } from 'lib/offer/offerTypes'

const bundleLabelMessages: Record<CruiseBundleImageCornerLabel, MessageDescriptor> = defineMessages({
  [CRUISE_BUNDLE_HOTEL_LABEL]: {
    id: 'YTJIZPQgudsTc34xUMg_',
    defaultMessage: 'Cruise + Hotel',
    description: 'Tag label for cruise booking that includes hotel accommodation',
  },
  [CRUISE_BUNDLE_FLIGHT_CREDITS_LABEL]: {
    id: 'Vsqk2aCOOtcfvLCrfwef',
    defaultMessage: 'Cruise + Flight credits',
    description: 'Search tile promo label when a cruise bundle includes flight credits but no hotel items.',
  },
  [CRUISE_BUNDLE_HOTEL_FLIGHT_CREDITS_LABEL]: {
    id: 'gkCGFU8iEqY1ZySPPxCZ',
    defaultMessage: 'Cruise + Hotel + Flight credits',
    description: 'Search tile promo label when a cruise bundle includes both hotel accommodation and flight credits.',
  },
})

interface Props {
  view: CruiseOfferView
  countdownTagPlace: Extract<TagPlace, 'offer-card' | 'offer-search-tile' | 'offer-gallery'>
}

const CRUISE_FAMILY_COUNTDOWN_SHOW_WITHIN_DAYS = 30

function CruiseImageCornerLabels({ view, countdownTagPlace }: Props) {
  const { offer, imageCornerLabels, evergreenInclusions, standardInclusions, luxPlusInclusions } = view
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const openBundleInclusionsModal = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (!isCruiseOffer(offer)) return

    showModal(<CruiseOfferTileInclusionsModal
      offer={offer}
      evergreenInclusions={evergreenInclusions}
      standardInclusions={standardInclusions}
      luxPlusInclusions={luxPlusInclusions}
    />)
  }, [evergreenInclusions, luxPlusInclusions, offer, showModal, standardInclusions])

  return <LabelGroup>
    {imageCornerLabels.map(label => {
      if (label.type === CRUISE_SPECIAL_OFFER_LABEL) {
        return <HighlightLabel key={label.type}>
          {intl.formatMessage({
            id: 'if0bcPpT5eaahDeyjgEG',
            defaultMessage: 'Special offer',
            description: 'Badge label for cruise special offer on offer card',
          })}
        </HighlightLabel>
      }

      if (label.type === CRUISE_FAMILY_PRICING_LABEL) {
        return <HighlightLabel key={label.type}>
          {intl.formatMessage({
            id: 'yQeqEA8cNTmbdQN6SGGi',
            defaultMessage: 'Exclusive family pricing',
            description: 'Special offer label on cruise search tiles for the Family of 4 promotional price',
          })}
        </HighlightLabel>
      }

      if (label.type === CRUISE_OFFER_COUNTDOWN_LABEL) {
        return <OfferUrgencyCountdownLabel
          key={label.type}
          endDate={label.endDate}
          prefixLabel={intl.formatMessage({
            id: 'aD7zby5ii2Scn3GvQ15M',
            defaultMessage: 'Offer',
            description: 'Prefix label used before the cruise deal countdown timer',
          })}
          tagPlace={countdownTagPlace}
          overrideShowWithinXDays={CRUISE_FAMILY_COUNTDOWN_SHOW_WITHIN_DAYS}
        />
      }

      return <PromotionLabel key={label.type} icon={<LineShipIcon aria-hidden />}>
        <Group direction="horizontal" gap={4} verticalAlign="center">
          {intl.formatMessage(bundleLabelMessages[label.type])}
          <IconButton
            kind="tertiary"
            variant="ghost"
            size="xsmall"
            shape="circle"
            onClick={openBundleInclusionsModal}
            aria-label={intl.formatMessage({
              id: 'RdCZpvczcWSuirV9basT',
              defaultMessage: 'View cruise inclusions',
              description: 'Accessible label for cruise bundled offer inclusions info button in search results',
            })}
          >
            <LineInfoCircleIcon colour="normal-contrast" aria-hidden />
          </IconButton>
        </Group>
      </PromotionLabel>
    })}
    <ProductTypeLabel productType={view.offer.productType} />
  </LabelGroup>
}

export default CruiseImageCornerLabels
